import { paperInventoryBussiness } from "api/bussiness/paper-material/paper-inventory";
import { paperMaterialBusiness } from "api/bussiness/paper-material/paper-material-bussiness";
import {
  IControl,
  OptionItem,
} from "commons/ultilities/dynamic-form/models/control";
import { debounce } from "lodash";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useMemo } from "react";

export type GetArrayItemFn = (
  arrayName: string,
  rowIndex: number,
  controlName: string,
) => IControl;

interface UseGoodsIssueItemHandlersParams {
  getArrayItemControl: GetArrayItemFn;
  getRollNumberOptions: (materialId: string) => Promise<OptionItem[]>;
  getBrandByRollNumber: (rollNumber: string) => string;
}

export const useGoodsIssueItemHandlers = ({
  getArrayItemControl,
  getRollNumberOptions,
  getBrandByRollNumber,
}: UseGoodsIssueItemHandlersParams) => {
  const { setProp } = useStores();
  const fillOptionItemsForRollNumber = useCallback(
    async (rollNumberCtr: IControl, materialId?: string) => {
      if (materialId) {
        const rollNumberAsOptionItems = await getRollNumberOptions(materialId);
        rollNumberCtr.setProp("optionItems", rollNumberAsOptionItems);
      } else {
        rollNumberCtr.setProp("optionItems", []);
      }
    },
    [getRollNumberOptions],
  );

  const materialIdChangedHandler = useCallback(
    async (materialIdCtr: IControl, rowIndex: number) => {
      const rollNumberCtr = getArrayItemControl(
        "items",
        rowIndex,
        "rollNumber",
      );
      const branchCtr = getArrayItemControl("items", rowIndex, "brand");
      branchCtr.setValueAndValidate("");
      rollNumberCtr.setValueAndValidate("");
      await fillOptionItemsForRollNumber(rollNumberCtr, materialIdCtr.value);
    },
    [fillOptionItemsForRollNumber, getArrayItemControl],
  );

  const rollNumberChangedHandler = useCallback(
    (rollNumberCtr: IControl, rowIndex: number) => {
      const branchCtr = getArrayItemControl("items", rowIndex, "brand");
      const brand = getBrandByRollNumber(rollNumberCtr.value) ?? "";
      branchCtr.setValueAndValidate(brand);
      branchCtr.setProp("disabled", true);
    },
    [getArrayItemControl, getBrandByRollNumber],
  );

  const calculatePcsAmountHandler = useCallback(
    (_control: IControl, rowIndex: number) => {
      const sheetQuantityControl = getArrayItemControl(
        "items",
        rowIndex,
        "sheetQuantity",
      );
      const unitsPerSheetControl = getArrayItemControl(
        "items",
        rowIndex,
        "unitsPerSheet",
      );
      const pcsAmountControl = getArrayItemControl(
        "items",
        rowIndex,
        "pcsAmount",
      );
      if (sheetQuantityControl && unitsPerSheetControl && pcsAmountControl) {
        const sheetQuantity = sheetQuantityControl.value || 0;
        const unitsPerSheet = unitsPerSheetControl.value || 0;
        pcsAmountControl.setValueAndValidate(sheetQuantity * unitsPerSheet);
      }
    },
    [getArrayItemControl],
  );
  // Nếu lấy hết = true thì sẽ lấy hết, call api lấy số lượng tồn kho hiện tại,
  const calculateAmountByTakeAllHandler = useCallback(
    async (materialId: string, rowIndex: number) => {
      const rollNumberCtr = getArrayItemControl(
        "items",
        rowIndex,
        "rollNumber",
      );
      if (!rollNumberCtr.value) return 0;
      const res = await paperInventoryBussiness.getAvailableStockAmount(
        materialId,
        rollNumberCtr.value,
      );
      return res?.stockAmount || 0;
    },
    [getArrayItemControl],
  );

  //Tự động tính dưới “GSM” x “Khổ” x “Dài” x “S/L Tờ” / 10.000.000
  const calculateAmountByNotTakeAllHandler = useCallback(
    async (materialId: string, rowIndex: number) => {
      let amount = 0;
      const lengthCtr = getArrayItemControl("items", rowIndex, "length");
      const sheetQuantityControl = getArrayItemControl(
        "items",
        rowIndex,
        "sheetQuantity",
      );
      const length = lengthCtr.value || 0;
      const sheetQuantity = sheetQuantityControl.value || 0;
      if (!materialId || !length || !sheetQuantity) return 0;

      const paperMaterial = await paperMaterialBusiness.getById(materialId);

      const gsm = paperMaterial.gsm || 0;
      const width = paperMaterial.specificationSize || 0;

      amount = (gsm * width * length * sheetQuantity) / 10000000;

      return amount;
    },
    [getArrayItemControl],
  );

  const calculateAmountHandler = useMemo(
    () =>
      debounce(async (_control: IControl, rowIndex: number) => {
        try {
          setProp("appFreeze", true);
          const materialIdCtr = getArrayItemControl(
            "items",
            rowIndex,
            "materialId",
          );
          let amount = 0;
          const amountControl = getArrayItemControl(
            "items",
            rowIndex,
            "amount",
          );

          const isTakeAllCtr = getArrayItemControl(
            "items",
            rowIndex,
            "isTakeAll",
          );

          if (!materialIdCtr?.value) {
            return;
          }

          if (isTakeAllCtr.value) {
            // Nếu lấy hết = true thì sẽ lấy hết, call api lấy số lượng tồn kho hiện tại, nếu lấy hết = false thì sẽ lấy theo công thức
            amount = await calculateAmountByTakeAllHandler(
              materialIdCtr.value,
              rowIndex,
            );
          } else {
            amount = await calculateAmountByNotTakeAllHandler(
              materialIdCtr.value,
              rowIndex,
            );
          }

          amountControl.setValueAndValidate(amount);
        } finally {
          setProp("appFreeze", false);
        }
      }, 500),
    [
      calculateAmountByNotTakeAllHandler,
      calculateAmountByTakeAllHandler,
      getArrayItemControl,
      setProp,
    ],
  );

  const itemValueChangedHandler = useCallback(
    async (control: IControl, rowIndex: number) => {
      if (control.name === "materialId") {
        await materialIdChangedHandler(control, rowIndex);
      }

      if (control.name === "rollNumber") {
        rollNumberChangedHandler(control, rowIndex);
      }

      if (
        control.name === "sheetQuantity" ||
        control.name === "unitsPerSheet"
      ) {
        calculatePcsAmountHandler(control, rowIndex);
      }

      if (
        control.name === "isTakeAll" ||
        control.name === "materialId" ||
        control.name === "sheetQuantity" ||
        control.name === "length" ||
        control.name === "rollNumber"
      ) {
        calculateAmountHandler(control, rowIndex);
      }
    },
    [
      materialIdChangedHandler,
      rollNumberChangedHandler,
      calculatePcsAmountHandler,
      calculateAmountHandler,
    ],
  );

  return {
    fillOptionItemsForRollNumber,
    materialIdChangedHandler,
    rollNumberChangedHandler,
    calculatePcsAmountHandler,
    itemValueChangedHandler,
  };
};
